import {
  Table,
  type TableColumnDefinition,
  TableInputField,
} from "$components/table";
import { toast } from "$global-providers/toast-provider";
import { useUser } from "$global-providers/user-provider";
import ExpiryCell from "$routes/inventory/_lib/expiry-cell";
import { ProductOriginBadge } from "$routes/inventory/_lib/product-origin-badge";
import { gql } from "@apollo/client";
import { Box, CircularProgress, makeStyles } from "@material-ui/core";
import React, { useEffect } from "react";
import { useIntl } from "react-intl";
import { useDebouncedCallback } from "use-debounce";
import { number, object, ValidationError } from "yup";
import { useReviewStockTakeLotMutation } from "./__generated__/stock-take-summary-table.gql";
import { DifferenceChip } from "./difference-chip";

const summaryRowSchema = object({
  reviewedQuantity: number().min(0).integer(),
});

export interface StockTakeSummaryRow {
  id: string;
  name: string;
  expiryDate: string;
  initialQuantity: number;
  reviewedQuantity: number;
  quantityDifference: number;
  externalReferences?: ReadonlyArray<{ origin: string }>;
}

gql`
  mutation reviewStockTakeLot($input: ReviewStockTakeLotInput!) {
    reviewStockTakeLot(input: $input) {
      ... on StockTakeLotAddition {
        id
        quantityDifference
        stockTake {
          id
          aggregateInfo {
            totalLotCount
            reviewedLotCount
          }
        }
      }
      ... on StockTakeLotUpdate {
        id
        quantityDifference
        stockTake {
          id
          aggregateInfo {
            totalLotCount
            reviewedLotCount
          }
        }
      }
    }
  }
`;

const ReviewedInputField: React.FunctionComponent<{
  rowId: string;
  facilityId: string;
  initialValue: string;
  validationFieldPath: string;
}> = ({ rowId, facilityId, initialValue, validationFieldPath }) => {
  const [inputError, setInputError] = React.useState<ValidationError>();
  const [reviewStockTakeLot, { error, loading }] =
    useReviewStockTakeLotMutation();

  const debouncedSave = useDebouncedCallback(async (event: number | string) => {
    try {
      setInputError(undefined);
      const value = parseInt(String(event), 10);
      if (Number.isNaN(value) || value === null) {
        return;
      }

      // this will throw if not valid
      summaryRowSchema.validateSyncAt(
        validationFieldPath,
        { [validationFieldPath]: value }, // must be shape of the schema
      );

      await reviewStockTakeLot({
        variables: {
          input: { id: rowId, facilityId, reviewedQuantity: value },
        },
      });
    } catch (e) {
      if (e instanceof ValidationError) {
        setInputError(e);
      } else {
        throw e;
      }
    }
  }, 1000);

  useEffect(() => {
    if (error) toast.error(error.message);
  }, [error]);

  return (
    <TableInputField
      error={!!inputError}
      defaultValue={initialValue}
      inputProps={{ type: "number", min: "0" }}
      onChange={(event) => debouncedSave(event.target.value)}
      endAdornment={
        <div style={{ width: "16px", height: "16px" }}>
          {loading ? (
            <CircularProgress style={{ width: "16px", height: "16px" }} />
          ) : null}
        </div>
      }
    />
  );
};

function buildTableColumns({
  formatMessage,
  editableReviewedQuantity = false,
  facilityId,
}): TableColumnDefinition[] {
  return [
    {
      field: "name",
      headerName: formatMessage({
        id: "inventory.stockTakeSummary.table.columnLabel.name",
        defaultMessage: "MEDICATION",
      }),
      flex: 5,
      renderCell: (value: string, _index, _column, row) => (
        <Box display="flex" alignItems="center" gridGap={8}>
          <span>{value}</span>
          <ProductOriginBadge externalReferences={row?.externalReferences} />
        </Box>
      ),
    },
    {
      field: "expiryDate",
      headerName: formatMessage({
        id: "inventory.stockTakeSummary.table.columnLabel.expiryDate",
        defaultMessage: "EXPIRY DATE",
      }),
      flex: 2,
      renderCell: (value: string) => <ExpiryCell expiryDate={value} />,
    },
    {
      field: "initialQuantity",
      headerName: formatMessage({
        id: "inventory.stockTakeSummary.table.columnLabel.initialQuantity",
        defaultMessage: "INITIAL COUNT",
      }),
      flex: 2,
      alignContents: "right",
    },
    {
      field: "reviewedQuantity",
      headerName: formatMessage({
        id: "inventory.stockTakeSummary.table.columnLabel.reviewedQuantity",
        defaultMessage: "STOCKTAKE COUNT",
      }),
      flex: 2,
      alignContents: "right",
      renderCell: (value, _index, _columnDefinition, row) => {
        if (!editableReviewedQuantity) return value;

        return (
          <ReviewedInputField
            initialValue={value}
            rowId={row.id as string}
            facilityId={facilityId}
            validationFieldPath="reviewedQuantity"
          />
        );
      },
    },
    {
      field: "quantityDifference",
      headerName: formatMessage({
        id: "inventory.stockTakeSummary.table.columnLabel.quantityDifference",
        defaultMessage: "DIFFERENCE",
      }),
      flex: 1,
      alignContents: "right",
      renderCell: (value) => {
        return <DifferenceChip difference={value} />;
      },
    },
    {
      field: "lastStockTakeAt",
      headerName: formatMessage({
        id: "inventory.stockTakeSummary.table.columnLabel.lastStocktake",
        defaultMessage: "Last Stocktake",
      }),
      flex: 2,
      alignContents: "right",
      renderCell: (value) => {
        return value ? new Date(value).toLocaleDateString() : null;
      },
    },
  ];
}

interface Props {
  stockTakeRows: StockTakeSummaryRow[];
  totalRows: number;
  currentPage: number;
  pageSize: number;
  onPageChange: (newPage: number) => void;
  editableReviewedQuantity?: boolean;
  loading?: boolean;
}

const useStyles = makeStyles((_theme) => ({
  rowCellClass: {
    padding: 10,
  },
}));

const StockTakeSummaryTable = ({
  stockTakeRows,
  totalRows,
  currentPage,
  pageSize,
  onPageChange,
  editableReviewedQuantity = false,
  loading,
}: Props): JSX.Element => {
  const { formatMessage } = useIntl();
  const { facilityId } = useUser();
  const tableClasses = useStyles();
  const tableColumns = buildTableColumns({
    formatMessage,
    facilityId,
    editableReviewedQuantity,
  });

  return (
    <Table
      loading={loading}
      columns={tableColumns}
      rows={stockTakeRows ?? []}
      classes={{ rowCellClass: () => tableClasses.rowCellClass }}
      noDataMessage={formatMessage({
        id: "inventory.stockTakeSummary.list.noData",
        defaultMessage: "No lots found for this stocktake.",
      })}
      pagination={{
        page: currentPage,
        pageSize,
        totalRows,
        handlePageChange: onPageChange,
      }}
    />
  );
};

export default StockTakeSummaryTable;
